// front/src/pages/Receipts.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import "../Dashboard.css";
import Select from "react-select";

import Header from "../components/Header";
import Footer from "../components/Footer";
import ScrollToTop from "../components/ScrollTop";
import PhoneLoader from "../components/loader/PhoneLoader";
import { useAuth } from "../provider/authProvider";
import useSedeGate from "../hooks/useSedeGate";

// Toasts
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Validador de precio (muta e.target.value)
import { handlePriceInput } from "../utils/inputUtils";

const API = process.env.REACT_APP_API || "";

// ===== Métodos de pago =====
const PM_OPTIONS = [
    { value: "1", label: "Efectivo" },
    { value: "2", label: "Yape" },
    { value: "3", label: "Tarjeta" },
    { value: "4", label: "Transferencia" },
    { value: "5", label: "Culqui POS" },
];
// helper para mostrar etiqueta en historial
const PM_LABELS = PM_OPTIONS.reduce((acc, o) => {
    acc[o.value] = o.label;
    return acc;
}, {});

// ⬇️ Helper para obtener un ISO en hora de Perú (UTC-5)
function nowLimaISO() {
    // "sv-SE" entrega "YYYY-MM-DD HH:mm:ss"
    const fmt = new Intl.DateTimeFormat("sv-SE", {
        timeZone: "America/Lima",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
    });
    // "2025-09-12 15:30:00" -> "2025-09-12T15:30:00-05:00"
    return fmt.format(new Date()).replace(" ", "T") + "-05:00";
}

const formatCreationDate = (value) => {
    if (!value) return "-";

    const raw = String(value).replace("T", " ").slice(0, 19);
    const [datePart, timePart = ""] = raw.split(" ");
    const [year, month, day] = datePart.split("-");

    if (!year || !month || !day) return value;

    return `${day}-${month}-${year}${timePart ? ` ${timePart}` : ""}`;
};

export default function Receipts(props) {
    const { user, rol } = useAuth();
    useSedeGate({ user, rol });

    // const canEmit = Number(rol) === 3 || Number(user?.role) === 3 || Number(user?.rol) === 3;
    const canEmit = true;

    const [loading, setLoading] = useState(false);

    const [dniOptions, setDniOptions] = useState([]);
    const [dniSel, setDniSel] = useState(null);

    const [creditOptions, setCreditOptions] = useState([]);
    const [creditSel, setCreditSel] = useState(null);

    // selección múltiple de cuotas
    const [selectedInst, setSelectedInst] = useState([]); // array de ids
    const toggleInst = (id) => {
        setSelectedInst((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const [modeTotal, setModeTotal] = useState(false);
    const [ppAmount, setPpAmount] = useState(""); // monto pronto pago

    // método de pago (ID de 1..5)
    const [pmId, setPmId] = useState("1");

    const [result, setResult] = useState(null);

    // Historial
    const [history, setHistory] = useState([]);
    const [q, setQ] = useState("");
    const [page, setPage] = useState(1);
    const pageSize = 25;

    const authHeaders = () => ({
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
        },
    });

    const loadHistory = async () => {
        try {
            const { data } = await axios.get(`${API}/receipts`, authHeaders());
            setHistory(data?.data || []);
        } catch (e) {
            console.error("[receipts/list] error:", e?.response?.data || e.message);
            setHistory([]);
        }
    };

    // utils
    const toNum = (s) => Number(String(s || "0").replace(/[^\d.]/g, "")) || 0;
    const onPPChange = (e) => {
        handlePriceInput(e); // tu util muta el valor
        setPpAmount(e.target.value ?? "");
    };

    // 1) DNIs + historial
    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const { data } = await axios.get(`${API}/receipts/dnis`, authHeaders());
                setDniOptions(
                    (data?.data || []).map((x) => ({
                        value: x.fr_dni,
                        label: `${x.fr_dni} — ${x.fr_full_name}`,
                    }))
                );
            } catch (e) {
                alert("No se pudieron cargar los DNIs");
            } finally {
                setLoading(false);
            }
        })();

        loadHistory();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 2) Créditos por DNI
    const onDniChange = async (opt) => {
        setDniSel(opt);
        setCreditSel(null);
        setResult(null);
        setSelectedInst([]);
        setModeTotal(false);
        setPpAmount("");
        setLoading(true);
        try {
            const { data } = await axios.get(
                `${API}/receipts/credits/${opt.value}`,
                authHeaders()
            );
            const opts = (data?.data || []).map((c) => ({
                value: c.fr_id,
                label: `${c.fr_code_contract} · ${c.summary.status} · pend: ${c.summary.pendingCount
                    } · ${c.contract_date ? new Date(c.contract_date).toLocaleDateString() : "—"
                    }`,
                raw: c,
            }));
            setCreditOptions(opts);
            const firstActive =
                opts.find((o) => o.raw.summary.status === "Activo") || opts[0];
            setCreditSel(firstActive || null);
        } catch (e) {
            alert("No se pudieron cargar los créditos del DNI.");
        } finally {
            setLoading(false);
        }
    };

    // 3) Lista de cuotas
    const days = useMemo(() => creditSel?.raw?.fr_days_payment || [], [creditSel]);

    // ⬅️ true si la frecuencia es mensual (solo para visualizar #+1)
    const isMonthly = useMemo(() => {
        const f = creditSel?.raw?.fr_payment_frequency || "";
        return /mensual/i.test(f);
    }, [creditSel]);

    // 4) Generar PDF + DB
    const [justCreatedTxs, setJustCreatedTxs] = useState(new Set());
    useEffect(() => {
        if (justCreatedTxs.size === 0) return;
        const t = setTimeout(() => setJustCreatedTxs(new Set()), 60000); // limpia resaltado en 60s
        return () => clearTimeout(t);
    }, [justCreatedTxs]);

    const handleGenerate = async () => {
        if (!dniSel || !creditSel) return;

        if (!pmId) {
            alert("Selecciona un método de pago.");
            return;
        }

        if (!modeTotal && selectedInst.length === 0) {
            alert("Selecciona al menos una cuota");
            return;
        }

        setLoading(true);
        try {
            // Total de cuotas del crédito (preferir fr_num_fee; fallback: cantidad de días)
            const totalFees =
                Number(creditSel?.raw?.fr_num_fee) || (days?.length || 0) || null;

            // ⬇️ Enviamos “ahora” en hora de Perú + total de cuotas al backend
            const base = {
                fr_id: creditSel.raw.fr_id,
                dni: dniSel.value,
                payment_method_id: Number(pmId),
                created_at: nowLimaISO(),
                total_fees: totalFees, // info complementaria
            };

            if (modeTotal) {
                // PRONTO PAGO: un solo recibo
                const monto = toNum(ppAmount);
                if (!monto || monto <= 0) {
                    setLoading(false);
                    alert("Ingresa un monto válido para el pronto pago.");
                    return;
                }
                const body = {
                    ...base,
                    type: "total",
                    installmentId: null,
                    total_amount: monto,
                };
                const { data } = await axios.post(`${API}/receipts`, body, authHeaders());
                setResult(data);
                setJustCreatedTxs((prev) => new Set([...prev, data?.transaction].filter(Boolean)));
                toast.success("Comprobante creado con éxito", { theme: "colored" });
            } else {
                // VARIAS CUOTAS: N recibos
                const okTx = [];
                const fails = [];
                for (const id of selectedInst) {
                    const body = { ...base, type: "cuota", installmentId: Number(id) };
                    try {
                        const { data } = await axios.post(`${API}/receipts`, body, authHeaders());
                        okTx.push(data?.transaction);
                    } catch {
                        fails.push(id);
                    }
                }
                if (okTx.length) {
                    setJustCreatedTxs((prev) => {
                        const s = new Set(prev);
                        okTx.filter(Boolean).forEach((t) => s.add(t));
                        return s;
                    });
                    toast.success(`Se crearon ${okTx.length} recibo(s).`, { theme: "colored" });
                }
                if (fails.length) toast.error(`No se pudo crear: ${fails.join(", ")}`);
                setSelectedInst([]);
            }

            await loadHistory();
        } catch (e) {
            const msg =
                e?.response?.data?.error ||
                e.message ||
                "No se pudo generar el/los recibo(s).";
            alert(msg);
        } finally {
            setLoading(false);
        }
    };

    // 5) Filtro + paginación
    const filtered = useMemo(() => {
        const k = q.trim().toLowerCase();
        if (!k) return history;
        return history.filter((r) => {
            const hay = `
                ${r?.dni || ""} 
                ${r?.fr_full_name || ""} 
                ${r?.fr_last_name || ""}
                ${r?.id_transaction || ""} 
                ${r?.fk_id_credit || ""} 
                ${r?.user_name || ""} 
                ${r?.sede_name || r?.sd_sede || ""}
                ${r?.user_last_name || ""}`.toLowerCase();
            return hay.includes(k);
        });
    }, [history, q]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
    const pageSafe = Math.min(page, totalPages);
    const rows = useMemo(() => {
        const start = (pageSafe - 1) * pageSize;
        return filtered.slice(start, start + pageSize);
    }, [filtered, pageSafe]);

    const fileUrl = (name) =>
        name ? process.env.REACT_APP_API + `/data-files/receipts/${name}` : undefined;

    return (
        <div>
            {loading && <PhoneLoader />}
            <ScrollToTop />
            <Header user={user} namePage={props?.namePage || "Recibos"} />

            <section className="sectionBody sectionBodyRegister">
                <div className="containerRegisterHeaderWrapper">
                    <div className="containerCustom">
                        <div className="headerRegister">
                            <div className="headerOne">
                                <div className="headerItem fullName">
                                    <div style={{ fontWeight: 700 }}>Generar Recibos</div>
                                    <div
                                        className="sedeLocation"
                                        style={{ position: "relative", top: 1.1 }}
                                    >
                                        Emite por cuota o pronto pago
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="containerCustom">
                    <div
                        style={{
                            background: "#fff",
                            borderRadius: 12,
                            boxShadow: "0 4px 18px rgba(0,0,0,.06)",
                            marginTop: 14,
                        }}
                    >
                        <div
                            style={{
                                padding: 20,
                                display: "grid",
                                gridTemplateColumns: "1fr 1.2fr",
                                gap: 16,
                            }}
                        >
                            {/* Columna izquierda */}
                            <div>
                                <label
                                    style={{ fontWeight: 600, marginBottom: 6, display: "block" }}
                                >
                                    Buscar por DNI
                                </label>
                                <Select
                                    options={dniOptions}
                                    value={dniSel}
                                    onChange={onDniChange}
                                    placeholder="Selecciona un DNI"
                                />

                                {creditOptions.length > 0 && (
                                    <>
                                        <div style={{ height: 10 }} />
                                        <label
                                            style={{ fontWeight: 600, marginBottom: 6, display: "block" }}
                                        >
                                            Seleccionar crédito
                                        </label>
                                        <Select
                                            options={creditOptions}
                                            value={creditSel}
                                            onChange={(o) => {
                                                setCreditSel(o);
                                                setSelectedInst([]);
                                                setModeTotal(false);
                                                setPpAmount("");
                                                setResult(null);
                                            }}
                                            placeholder="Selecciona un crédito"
                                        />

                                        {creditSel && (
                                            <div
                                                style={{
                                                    marginTop: 12,
                                                    fontSize: 13,
                                                    color: "#444",
                                                    lineHeight: 1.6,
                                                }}
                                            >
                                                <div>
                                                    <strong>Cliente:</strong>{" "}
                                                    {creditSel.raw.fr_full_name +
                                                        " " +
                                                        creditSel.raw.fr_last_name}
                                                </div>
                                                <div>
                                                    <strong>Equipo:</strong> {creditSel.raw.fr_phone_name}{" "}
                                                    {creditSel.raw.fr_imei
                                                        ? `(IMEI ${creditSel.raw.fr_imei})`
                                                        : ""}
                                                </div>
                                                <div>
                                                    <strong>Frecuencia:</strong>{" "}
                                                    {creditSel.raw.fr_payment_frequency} ·{" "}
                                                    <strong>Cuota:</strong> S/{" "}
                                                    {Number(creditSel.raw.fr_fee).toFixed(2)}
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>

                            {/* Columna derecha */}
                            <div>
                                {creditSel ? (
                                    <>
                                        {/* Método de pago (aplica a ambos modos) */}
                                        <div style={{ marginBottom: 10 }}>
                                            <label
                                                style={{ fontWeight: 600, display: "block", marginBottom: 6 }}
                                            >
                                                Método de pago
                                            </label>
                                            <select
                                                value={pmId}
                                                onChange={(e) => setPmId(e.target.value)}
                                                style={{
                                                    width: 220,
                                                    height: 36,
                                                    borderRadius: 8,
                                                    border: "1px solid #cfd3d8",
                                                    padding: "0 10px",
                                                    fontWeight: 600,
                                                    background: "#fff",
                                                }}
                                            >
                                                {PM_OPTIONS.map((o) => (
                                                    <option key={o.value} value={o.value}>
                                                        {o.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Toggle pronto pago */}
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 8,
                                                marginBottom: 8,
                                            }}
                                        >
                                            <input
                                                id="modeTotal"
                                                type="checkbox"
                                                checked={modeTotal}
                                                onChange={(e) => {
                                                    const v = e.target.checked;
                                                    setModeTotal(v);
                                                    if (v) setSelectedInst([]); // limpiar selección si pasa a pronto pago
                                                }}
                                            />
                                            <label htmlFor="modeTotal">
                                                Pronto pago (monto total pendiente)
                                            </label>
                                        </div>

                                        {/* Input de monto para pronto pago */}
                                        {modeTotal && (
                                            <div style={{ marginBottom: 12 }}>
                                                <label
                                                    style={{ fontWeight: 600, display: "block", marginBottom: 6 }}
                                                >
                                                    Monto pronto pago
                                                </label>
                                                <input
                                                    type="text"
                                                    inputMode="decimal"
                                                    placeholder="S/ 0.00"
                                                    value={ppAmount}
                                                    onChange={onPPChange}
                                                    style={{
                                                        width: 200,
                                                        height: 36,
                                                        borderRadius: 8,
                                                        border: "1px solid #cfd3d8",
                                                        padding: "0 10px",
                                                        fontWeight: 700,
                                                    }}
                                                />
                                                <div style={{ fontSize: 12, color: "#666", marginTop: 6 }}>
                                                    {toNum(ppAmount) <= 0 ? (
                                                        "Ingresa un monto mayor a 0 para generar el recibo."
                                                    ) : (
                                                        <>
                                                            Este monto se usará como <strong>pago total</strong>{" "}
                                                            del recibo.
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {!modeTotal && selectedInst.length > 0 && (
                                            <div style={{ marginTop: 8, fontSize: 12, color: "#555" }}>
                                                {selectedInst.length} cuota(s) seleccionada(s)
                                            </div>
                                        )}

                                        {/* Lista de cuotas cuando NO es pronto pago */}
                                        {!modeTotal && (
                                            <div
                                                style={{
                                                    maxHeight: 360,
                                                    overflow: "auto",
                                                    border: "1px solid #eee",
                                                    borderRadius: 8,
                                                    padding: 12,
                                                }}
                                            >
                                                {days.map((d, idx) => {
                                                    const disabled = Number(d.state) === 0; // pagada
                                                    const baseId = Number(d.id ?? idx);
                                                    const displayId = isMonthly ? baseId + 1 : baseId; // ⬅️ sumar 1 solo para mostrar si es mensual
                                                    return (
                                                        <label
                                                            key={d.id ?? idx}
                                                            style={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: 10,
                                                                padding: "6px 4px",
                                                                opacity: disabled ? 0.55 : 1,
                                                            }}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                disabled={disabled}
                                                                checked={selectedInst.includes(Number(d.id))}
                                                                onChange={() => toggleInst(Number(d.id))}
                                                            />
                                                            <div
                                                                style={{
                                                                    display: "grid",
                                                                    gridTemplateColumns: "120px 140px 1fr",
                                                                    gap: 8,
                                                                    width: "100%",
                                                                }}
                                                            >
                                                                <span>
                                                                    <strong>#</strong> {displayId}
                                                                </span>
                                                                <span>
                                                                    <strong>Fecha:</strong> {d.date}
                                                                </span>
                                                                <span>
                                                                    <strong>Monto:</strong> S/{" "}
                                                                    {Number(d.cuota_final || d.cuota).toFixed(2)}{" "}
                                                                    {disabled ? "" : "· Pagada"}
                                                                </span>
                                                            </div>
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {/* Botón */}
                                        <div
                                            style={{
                                                marginTop: 20,
                                                display: "flex",
                                                justifyContent: "center",
                                            }}
                                        >
                                            <button
                                                onClick={handleGenerate}
                                                disabled={
                                                    !dniSel ||
                                                    !creditSel ||
                                                    (!modeTotal ? selectedInst.length === 0 : toNum(ppAmount) <= 0) ||
                                                    !pmId ||
                                                    loading
                                                }
                                                style={{
                                                    background: "#0d6e3a",
                                                    color: "#fff",
                                                    padding: "10px 18px",
                                                    borderRadius: 10,
                                                    border: "none",
                                                    cursor: "pointer",
                                                    fontWeight: 700,
                                                }}
                                            >
                                                {loading ? "Generando…" : "Generar Comprobante"}
                                            </button>
                                        </div>
                                    </>
                                ) : (
                                    <div style={{ color: "#777" }}>
                                        <div style={{ fontWeight: 700, textAlign: "center" }}>
                                            Selecciona un DNI y luego un crédito para ver las cuotas…
                                        </div>
                                        <div
                                            style={{
                                                marginTop: 20,
                                                display: "flex",
                                                justifyContent: "center",
                                            }}
                                        >
                                            <button
                                                onClick={handleGenerate}
                                                disabled={true}
                                                style={{
                                                    background: "#0d6e3a",
                                                    color: "#fff",
                                                    padding: "10px 18px",
                                                    borderRadius: 10,
                                                    border: "none",
                                                    cursor: "not-allowed",
                                                    fontWeight: 700,
                                                    opacity: 0.6,
                                                }}
                                            >
                                                Generar file
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* ======= HISTORIAL ======= */}
                    <div
                        style={{
                            background: "#fff",
                            borderRadius: 12,
                            boxShadow: "0 4px 18px rgba(0,0,0,.06)",
                            marginTop: 18,
                            padding: 16,
                        }}
                    >
                        <div
                            className="containerFlexSpace"
                            style={{ alignItems: "center", marginBottom: 10 }}
                        >
                            <h3 style={{ margin: 0, fontSize: 18, color: "#4d0084" }}>
                                Historial de recibos
                            </h3>
                            <div>
                                <input
                                    value={q}
                                    onChange={(e) => {
                                        setQ(e.target.value);
                                        setPage(1);
                                    }}
                                    placeholder="Filtrar por DNI / cliente / transacción…"
                                    style={{
                                        height: 36,
                                        borderRadius: 8,
                                        border: "1px solid #cfd3d8",
                                        padding: "0 10px",
                                        minWidth: 320,
                                    }}
                                />
                            </div>
                        </div>

                        <div className="registerTable registerTable5">
                            <table>
                                <thead>
                                    <tr>
                                        <th style={{ width: 70 }}>ID</th>
                                        <th>Transacción</th>
                                        <th>Seller</th>
                                        <th>DNI</th>
                                        <th>Cliente</th>
                                        <th>Sede</th>
                                        <th>Crédito</th>
                                        <th>Medio</th>
                                        <th>Archivo</th>
                                        <th>Fecha</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.length === 0 ? (
                                        <tr>
                                            <td colSpan={9} style={{ padding: 16 }}>
                                                No hay resultados.
                                            </td>
                                        </tr>
                                    ) : (
                                        rows.map((r) => {
                                            const isNew = justCreatedTxs.has(r.id_transaction);
                                            const pm =
                                                r.payment_method_id != null
                                                    ? PM_LABELS[String(r.payment_method_id)] ||
                                                    r.payment_method_id
                                                    : "-";
                                            return (
                                                <tr
                                                    key={r.id}
                                                    style={{
                                                        background: isNew ? "#fff7e6" : undefined,
                                                        transition: "background-color .3s ease",
                                                    }}
                                                    title={isNew ? "Recibo recién creado" : undefined}
                                                >
                                                    <td>{r.id}</td>
                                                    <td>{r.id_transaction}</td>
                                                    <td>
                                                        {[r.user_name, r.user_last_name]
                                                            .filter(Boolean)
                                                            .join(" ") || "-"}
                                                    </td>
                                                    <td>{r.dni}</td>
                                                    <td>
                                                        {[r.fr_full_name, r.fr_last_name]
                                                            .filter(Boolean)
                                                            .join(" ") || "-"}
                                                    </td>
                                                    <td>{r.sede_name || r.sd_sede || "-"}</td>
                                                    <td>#{r.fk_id_credit}</td>
                                                    <td>{pm}</td>
                                                    <td>
                                                        <div>
                                                            {r.name_file ? (
                                                                <a
                                                                    className="aview"
                                                                    href={fileUrl(r.name_file)}
                                                                    target="_blank"
                                                                    rel="noreferrer"
                                                                >
                                                                    Ver PDF
                                                                </a>
                                                            ) : (
                                                                "-"
                                                            )}
                                                        </div>
                                                    </td>

                                                    <td>
                                                        {formatCreationDate(r.creation_date)}
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* paginación simple */}
                        <div className="boxPagination">
                            <div style={{ gap: 10 }}>
                                <button
                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                    disabled={pageSafe <= 1}
                                    style={{
                                        padding: "6px 10px",
                                        borderRadius: 6,
                                        border: "1px solid #cfd3d8",
                                        background: pageSafe <= 1 ? "#f3f4f6" : "#fff",
                                        cursor: pageSafe <= 1 ? "not-allowed" : "pointer",
                                        marginRight: 8,
                                    }}
                                >
                                    ← Anterior
                                </button>
                                <span>
                                    Página <strong>{pageSafe}</strong> de{" "}
                                    <strong>{totalPages}</strong>
                                </span>
                                <button
                                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                    disabled={pageSafe >= totalPages}
                                    style={{
                                        padding: "6px 10px",
                                        borderRadius: 6,
                                        border: "1px solid #cfd3d8",
                                        background: pageSafe >= totalPages ? "#f3f4f6" : "#fff",
                                        cursor: pageSafe >= totalPages ? "not-allowed" : "pointer",
                                        marginLeft: 8,
                                    }}
                                >
                                    Siguiente →
                                </button>
                            </div>
                        </div>
                    </div>
                    {/* ======= /HISTORIAL ======= */}
                </div>
            </section>

            <Footer />
        </div>
    );
}
